import axios from "axios";

/**
 * Utility functions for Web Push setup in React (TypeScript)
 * Requires: a service worker file in /public/service-worker.js
 */
export const API_URL_PUSH = `${import.meta.env.VITE_API_BASE_URL}notifications`;

function checkAvailability(): boolean {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service Worker not supported.");
    return false;
  }
  if (!("PushManager" in window)) {
    console.warn("Push notifications not supported.");
    return false;
  }
  return true;
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!checkAvailability()) return null;

  try {
    /*
    const registration = await navigator.serviceWorker.register(
      "/service-worker.js"
    );
*/
    // The app's worker (sw-custom.js) handles push too. Registering sw.js
    // would replace it, and with it the files cached for offline use.
    const registration =
      (await navigator.serviceWorker.getRegistration()) ??
      (await navigator.serviceWorker.register("/sw.js"));

    console.log("✅ Service worker registered:", registration);
    return registration;
  } catch (err) {
    console.error("❌ Service worker registration failed:", err);
    return null;
  }
}

/**
 * Request permission for notifications, handling older callback-style APIs.
 */
function askPermission(): Promise<NotificationPermission> {
  return new Promise((resolve, reject) => {
    const permissionResult = Notification.requestPermission(
      (result: NotificationPermission) => {
        resolve(result);
      }
    );

    if (permissionResult) {
      permissionResult.then(resolve, reject);
    }
  }).then((permissionResult) => {
    if (permissionResult !== "granted") {
      throw new Error("Notification permission not granted.");
    }
    return permissionResult;
  });
}

/**
 * Get the current notification permission state.
 */
function getNotificationPermissionState(): Promise<
  PermissionState | NotificationPermission
> {
  if ("permissions" in navigator && navigator.permissions.query) {
    return navigator.permissions
      .query({ name: "notifications" as PermissionName })
      .then((result) => result.state);
  }
  return Promise.resolve(Notification.permission);
}

/**
 * Subscribe the user to push notifications.
 * @param registration - The Service Worker registration object.
 * @param publicKey - The VAPID public key (Base64 URL-safe format).
 */
async function subscribeUserToPush(
  registration: ServiceWorkerRegistration,
  publicKey: string
): Promise<string> {
  if (!registration.pushManager) {
    throw new Error("Push manager unavailable.");
  }

  if (!publicKey) {
    throw new Error("No public VAPID key provided.");
  }

  let key;
  try {
    key = urlBase64ToUint8Array(publicKey);
  } catch (err) {
    throw new Error("Invalid public VAPID key.");
  }
  let subscription;
  try {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key.buffer as ArrayBuffer, // 👈 cast fixes TS
    });
  } catch (err) {
    throw new Error("Push subscription failed.");
  }

  const authToken = localStorage.getItem("daex-auth-token");

  const data = {
    subscription,
  };

  if (!authToken) {
    throw Error("No auth token found in local storage.");
  }

  const response = await axios.post<any>(`${API_URL_PUSH}/subscribe`, data, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    withCredentials: true,
  });

  if (response) return response.data;
  throw new Error("No data received.");
}

/**
 * Convert Base64 URL-safe string to Uint8Array (required for VAPID key).
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

const settingsService = {
  subscribeUserToPush,
  getNotificationPermissionState,
  askPermission,
  registerServiceWorker,
};
export default settingsService;
